import React, { useCallback, useState } from 'react';
import { Download, Share2, ArrowRight } from 'lucide-react';
import Carousel from '../Carousel';
import { downloadImage } from '../../../utils/downloadImage';

/**
 * Global reusable reveal/share card widget — shows chosen words + photo as a shareable moment.
 * Props: photo, photos (optional — when there's more than one, renders as a swipeable carousel
 * with a dot rail, matching the "3rd screen" effect in the design), words, intro, productLine,
 * tagline, onContinue
 */
const MagicRevealCard = ({
  photo,
  photos,
  words = [],
  intro = 'Love at first sight',
  productLine = 'Ice-cold Coke',
  tagline = 'A recipe for magic',
  onContinue
} = {}) => {
  const [sharing, setSharing] = useState(false);

  const gallery = (photos?.length > 0 ? photos : photo ? [photo] : []).filter(Boolean);
  const primaryPhoto = gallery[0] || photo;
  const isCarousel = gallery.length > 1;

  const wordsLine = words.length > 0 ? words.join(', ') : null;
  const shareText = `${intro}\n${wordsLine || ''}\n${productLine}\n${tagline}`.trim();

  const overlay = (
    <div className="magic-reveal-overlay">
      <div className="magic-reveal-intro">{intro}</div>
      {wordsLine && <div className="magic-reveal-words">{wordsLine}</div>}
      <div className="magic-reveal-product">{productLine}</div>
      <div className="magic-reveal-tagline">{tagline}</div>
    </div>
  );

  const handleDownload = useCallback(() => {
    downloadImage(primaryPhoto, 'my-magic-moment');
  }, [primaryPhoto]);

  const handleShare = useCallback(async () => {
    setSharing(true);
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Magic Moment', text: shareText });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(shareText);
      }
    } catch {
      /* user cancelled or share unsupported — no-op */
    } finally {
      setSharing(false);
    }
  }, [shareText]);

  return (
    <div className="magic-step magic-reveal-card px-4 py-4">
      {isCarousel ? (
        <div className="magic-reveal-visual magic-reveal-visual--carousel">
          <Carousel
            variant="reveal"
            slides={gallery.map((src, index) => ({ id: index, image: src }))}
            renderSlide={(slide) => (
              <div className="magic-reveal-slide" style={{ backgroundImage: `url(${slide.image})` }}>
                {overlay}
              </div>
            )}
          />
        </div>
      ) : (
        <div className="magic-reveal-visual" style={primaryPhoto ? { backgroundImage: `url(${primaryPhoto})` } : undefined}>
          {overlay}
        </div>
      )}

      <div className="magic-reveal-actions">
        <button type="button" className="magic-icon-btn" onClick={handleDownload} disabled={!primaryPhoto}>
          <Download size={18} />
          Download
        </button>
        <button type="button" className="magic-icon-btn" onClick={handleShare} disabled={sharing}>
          <Share2 size={18} />
          {sharing ? 'Sharing…' : 'Share'}
        </button>
      </div>

      <button type="button" className="magic-fab" onClick={() => onContinue && onContinue()} aria-label="Continue">
        <ArrowRight size={22} />
      </button>
    </div>
  );
};

export default MagicRevealCard;
